import { memo, useCallback, useMemo } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSearchParams } from 'react-router-dom';

import type { VereinaStop } from '@/types';
import type { PickingInfo } from '@deck.gl/core';

import DeckGLOverlay from '@/components/map/train-marker/deckgl-overlay';
import usePinnedItem from '@/hooks/use-pinned-item';
import useSocketSubscription from '@/hooks/use-socket-subscription';
import {
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_STATIONS,
  SEARCH_PARAM_VEREINA_STATUS,
} from '@/utils/constants/queries';
import { useDeckCursorHover } from '@/utils/helpers/train-simulation/use-deck-cursor-hover';
import { getVereinaDeckLayers } from '@/utils/helpers/train-simulation/vereina-deck-layers';

const VereinaInfoMarker = () => {
  const { current: mapRef } = useMap();
  const [, setSearchParams] = useSearchParams();

  const { data: vereinaData } = useSocketSubscription<VereinaStop[]>('vereina');

  const handleHover = useDeckCursorHover(mapRef);
  const { pinnedItemId, pinnedItemType } = usePinnedItem();

  const handleClick = useCallback(
    (info: PickingInfo) => {
      const stopId = (info?.object as VereinaStop)?.stop?.id;

      if (stopId) {
        const status = (info?.object as VereinaStop)?.waitingTime?.value;

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set(SEARCH_PARAM_SHOW, SEARCH_PARAM_STATIONS);
          next.set(SEARCH_PARAM_STATION_ID, stopId);

          if (pinnedItemId) {
            next.set(SEARCH_PARAM_LOCK_PIN, pinnedItemId);
          }

          if (pinnedItemType) {
            next.set(SEARCH_PARAM_LOCK_PIN_TYPE, pinnedItemType);
          }

          if (status) {
            next.set(SEARCH_PARAM_VEREINA_STATUS, status);
          }

          return next;
        });

        return true;
      }
    },
    [pinnedItemId, pinnedItemType]
  );

  const layers = useMemo(
    () => getVereinaDeckLayers(vereinaData || [], handleClick),
    [vereinaData, handleClick]
  );

  if (!mapRef) {
    return null;
  }

  return <DeckGLOverlay layers={layers} onHover={handleHover} />;
};

export default memo(VereinaInfoMarker);
